import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Calendar, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEpisode, useEpisodes } from "@/hooks/useEpisodes";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";
import PodcastEmbed from "@/components/PodcastEmbed";

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PodcastDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const { episode, loading, error } = useEpisode(slug);
  const { episodes } = useEpisodes();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center text-white/55">
          Lade…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !episode) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="font-display uppercase text-4xl">
            Folge nicht gefunden
          </h1>
          <Link
            to="/"
            className="inline-flex mt-8 px-5 py-3 btn-ghost text-sm uppercase tracking-wide"
          >
            <ArrowLeft size={16} className="mr-2" /> Zurück
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const content = episode[lang] || episode.de;
  const related = (episodes || [])
    .filter((e) => e.slug !== episode.slug)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <article
        data-testid="podcast-detail"
        className="pt-28 md:pt-32 pb-20 bg-[#0A0A0A]"
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link
            to="/#podcast"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-redflag transition-colors font-mono-plex uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> {t.podcastDetail.back}
          </Link>

          <div className="mt-8 font-mono-plex text-[11px] uppercase tracking-widest text-redflag">
            EP {episode.number} · {episode.category}
          </div>
          <h1 className="font-display uppercase mt-4 text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {content.title}
          </h1>

          <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/60 font-mono-plex">
            <span className="inline-flex items-center gap-2">
              <Clock size={14} className="text-redflag" />{" "}
              {t.podcastDetail.duration}: {episode.duration}
            </span>
            <span className="inline-flex items-center gap-2">
              <Calendar size={14} className="text-redflag" />{" "}
              {t.podcastDetail.released}: {formatDate(episode.released_iso, lang)}
            </span>
          </div>

          <div className="mt-10 aspect-[16/9] overflow-hidden border border-white/10">
            <img
              src={episode.cover}
              alt={content.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <p className="text-lg md:text-xl text-white/80 leading-relaxed prose-rf">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content.intro}
                </ReactMarkdown>
              </p>

              <h3 className="mt-12 overline">{t.podcastDetail.keyTakeaways}</h3>
              <ul className="mt-5 space-y-3">
                {(content.takeaways || []).map((tk, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-base text-white/80"
                  >
                    <Check size={18} className="text-redflag mt-1 shrink-0" />
                    {tk}
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-4">
                <PodcastEmbed episode={episode} />
                <NewsletterMini lang={lang} />
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <div className="mt-24">
              <h3 className="overline">{t.podcastDetail.relatedTitle}</h3>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {related.map((r) => {
                  const c = r[lang] || r.de;
                  return (
                    <Link
                      key={r.slug}
                      to={`/podcast/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="group bg-[#0F0F0F] p-6 md:p-8 flex gap-5 hover:bg-[#161616] transition-colors"
                    >
                      <div className="w-24 h-24 shrink-0 overflow-hidden">
                        <img
                          src={r.cover}
                          alt={c.title}
                          className="w-full h-full object-cover grayscale-img"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="font-mono-plex text-[10px] uppercase tracking-widest text-redflag">
                          EP {r.number} · {r.category}
                        </div>
                        <div className="mt-2 font-display uppercase text-lg leading-tight text-white">
                          {c.title}
                        </div>
                        <div className="mt-2 text-xs text-white/55 line-clamp-2">
                          {c.teaser}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}

function NewsletterMini({ lang }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSub = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        email: email.trim(),
      });
      setDone(true);
      setEmail("");
    } catch (err) {
      // Newsletter sub failure: keep UX simple, but log for diagnostics.
      console.debug("podcast newsletter subscribe failed", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSub} className="border border-white/10 bg-[#0F0F0F] p-5">
      <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/55">
        {lang === "de" ? "Newsletter" : "Newsletter"}
      </div>
      <div className="mt-1 font-display uppercase text-lg text-white">
        {lang === "de" ? "Sonntags eine Mail." : "One Sunday email."}
      </div>
      {done ? (
        <p className="mt-3 text-sm text-redflag">
          {lang === "de" ? "Eingetragen ✓" : "Subscribed ✓"}
        </p>
      ) : (
        <div className="mt-4 flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={lang === "de" ? "deine@email.ch" : "you@email.com"}
            className="flex-1 bg-[#0A0A0A] border border-white/15 focus:border-redflag px-3 py-2 text-xs outline-none text-white placeholder:text-white/30"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-primary px-4 py-2 text-xs uppercase tracking-wide disabled:opacity-60"
          >
            {loading ? "…" : "OK"}
          </button>
        </div>
      )}
    </form>
  );
}
